import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import { colors } from "../../../styles/colors";

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  acceptedFileTypes: string;
  title?: string;
  dragDropText?: string;
  browseText?: string;
  acceptedTypesHint?: string;
}

/**
 * Generische Dialog-Komponente für den Upload von Dateien
 * Unterstützt Drag & Drop und Datei-Auswahl
 */
const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  onUpload,
  acceptedFileTypes,
  title = "Datei hochladen",
  dragDropText = "Datei hierher ziehen",
  browseText = "oder klicken zum Durchsuchen",
  acceptedTypesHint,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Prüft, ob der Dateityp akzeptiert wird
  const isFileTypeAccepted = (fileName: string): boolean => {
    const extensions = acceptedFileTypes.split(",").map((ext) => ext.trim());
    return extensions.some((ext) => fileName.endsWith(ext));
  };

  // Drag & Drop Handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const acceptedFile = files.find((f) => isFileTypeAccepted(f.name));

      if (acceptedFile) {
        setSelectedFile(acceptedFile);
      }
    },
    [acceptedFileTypes],
  );

  // File Input Handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isFileTypeAccepted(file.name)) {
      setSelectedFile(file);
    }
  };

  // Upload Handler
  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      setSelectedFile(null);
      onClose();
    }
  };

  // Dialog schließen und State zurücksetzen
  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {/* Drag & Drop Bereich */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${isDragOver ? colors.border.focus : colors.border.medium}`,
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            backgroundColor: isDragOver
              ? colors.state.hoverPrimarySubtle
              : "transparent",
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              borderColor: colors.border.focus,
              backgroundColor: colors.state.hover,
            },
          }}
          onClick={() => document.getElementById("file-input")?.click()}
        >
          <CloudUploadIcon
            sx={{ fontSize: 64, color: colors.border.focus, mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            {dragDropText}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {browseText}
          </Typography>
          {acceptedTypesHint && (
            <Typography variant="caption" color="textSecondary">
              {acceptedTypesHint}
            </Typography>
          )}

          {/* Hidden File Input */}
          <input
            id="file-input"
            type="file"
            accept={acceptedFileTypes}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </Box>

        {/* Ausgewählte Datei anzeigen */}
        {selectedFile && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: colors.state.hoverPrimaryMedium,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <FileIcon sx={{ color: colors.border.focus }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Abbrechen
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile}
          startIcon={<CloudUploadIcon />}
        >
          Hochladen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
